import {Alert, Button, DataStatus, openDeleteDialog} from 'platform/components';
import {Box, Grid, HStack, Icon, Show, Text, VStack} from 'platform/foundation';

import {useGetFieldDefinitionListQuery} from '@dms/api/settings';
import {DragHandle, SortableCardList} from '@dms/features/dnd';
import i18n from '@dms/i18n';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

import {EnumNameInput} from './EnumNameInput';
import {useUpdateEnum} from './hooks/useUpdateEnum';

const CONTAINER_ID = 'enum-options';

interface EnumDefinitionUpdateFormProps extends RequiredTestIdProps {
  definitionId: string;
  resourceId: string;
}

export function EnumDefinitionUpdateForm(props: EnumDefinitionUpdateFormProps) {
  const {
    data: definitions,
    isLoading: isLoadingDefinitions,
    isError,
    isFetching,
  } = useGetFieldDefinitionListQuery({
    resourceId: props.resourceId,
    type: 'tenant',
  });

  const definition = definitions?.find((definition) => definition.id === props.definitionId);
  const sortableItems =
    definition?.enumOptions?.map((option) => ({...option, id: option.uuid})) ?? [];

  const {
    remove,
    updatePriority,
    updateActive,
    add,
    isLoading: isUpdating,
  } = useUpdateEnum(props.definitionId, props.resourceId);

  const isLoading = isFetching || isUpdating;

  const handleAddEnumOption = () => add('', (sortableItems?.length ?? 0) + 1);

  return (
    <DataStatus
      isLoading={isLoadingDefinitions}
      isError={isError}
      isEmpty={!sortableItems?.length}
      spacing={10}
      data-testid={suffixTestId('enumDefinitionUpdateForm', props)}
      action={{
        title: i18n.t('general.actions.add'),
        onClick: handleAddEnumOption,
        leftIcon: 'content/add_circle',
      }}
    >
      <VStack spacing={4}>
        <Alert
          variant="info"
          type="banner"
          title={i18n.t('page.generalSettings.labels.enumValuesAlert')}
        />
        <VStack spacing={2} align="flex-start">
          <SortableCardList
            containerId={CONTAINER_ID}
            items={sortableItems}
            withHandle
            disableDropAnimation
            onReorder={(reorderedItems) => {
              updatePriority(reorderedItems.map((item) => item.uuid));
            }}
            renderCard={(item, index, handleProps) => {
              if (!definition) {
                return null;
              }

              return (
                <HStack align="flex-start" width="100%" spacing={2}>
                  <Box paddingTop={1}>
                    <DragHandle {...handleProps}>
                      <Icon value="action/drag_indicator" color="palettes.neutral.60.100" />
                    </DragHandle>
                  </Box>
                  <Box paddingTop={1} width={5}>
                    <Text
                      color="tertiary"
                      align="center"
                      data-testid={suffixTestId('enumValueIndex', props)}
                    >{`${index + 1}.`}</Text>
                  </Box>
                  <Box flex={1}>
                    <EnumNameInput
                      definition={definition}
                      enumOption={item}
                      data-testid={suffixTestId('enumValue', props)}
                    />
                  </Box>

                  <Box width={48}>
                    <Grid columns={2} spacing={2}>
                      <Show when={item.active}>
                        <Button
                          type="button"
                          leftIcon="action/visibility_off"
                          title={i18n.t('general.actions.hide')}
                          variant="secondary"
                          data-testid={suffixTestId('hideEnumValue', props)}
                          onClick={() => {
                            updateActive(item.uuid, false);
                          }}
                          isDisabled={isLoading}
                        />
                      </Show>

                      <Show when={!item.active}>
                        <Button
                          type="button"
                          leftIcon="action/visibility"
                          title={i18n.t('general.actions.unhide')}
                          variant="secondary"
                          data-testid={suffixTestId('unhideEnumValue', props)}
                          onClick={() => {
                            updateActive(item.uuid, true);
                          }}
                          isDisabled={isLoading}
                        />
                      </Show>

                      <Button
                        type="button"
                        leftIcon="action/delete"
                        variant="dangerGhost"
                        title={i18n.t('general.actions.delete')}
                        onClick={() => {
                          openDeleteDialog({
                            onConfirm: () => remove(item.uuid),
                            text: i18n.t('page.generalSettings.labels.deleteEnumValueDialogText', {
                              name: item.name,
                            }),
                            'data-testid': suffixTestId('deleteEnumValue', props),
                          });
                        }}
                        isDisabled={item.active || isLoading}
                      />
                    </Grid>
                  </Box>
                </HStack>
              );
            }}
          />
          <Button
            leftIcon="content/add_circle"
            title={i18n.t('general.actions.addAnother')}
            size="small"
            data-testid={suffixTestId('addEnumValue', props)}
            variant="link"
            onClick={handleAddEnumOption}
          />
        </VStack>
      </VStack>
    </DataStatus>
  );
}
